// Web push subscription manager. Bridges the browser Push API to
// our /api/notifications/device-tokens registration so the server-
// side dispatcher (see server/src/services/notificationService.js)
// can fan deliveries out to the right subscription.
//
// Three top-level operations:
//   • isSupported()         — environment capability check
//   • status()              — current Notification.permission +
//                             existing subscription, no network
//   • enable()              — register SW, request permission,
//                             fetch VAPID public key, subscribe,
//                             POST to API
//   • disable()             — unsubscribe locally, DELETE from API
//
// The SW lives at /sw.js so it has root scope and can receive push
// events for the whole site.

import { request, jsonOptions } from '../api/client';

const SW_URL = '/sw.js';

// Routes service-worker → main-thread messages. The SW posts
// {type: 'og:navigate', url} when a notification is tapped and an
// existing tab is reused; the main thread navigates via React Router.
export function bindServiceWorkerMessages(navigate) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const handler = (event) => {
    const data = event.data;
    if (!data || data.type !== 'og:navigate' || !data.url) return;
    try { navigate(data.url); } catch {}
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

export function isSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function status() {
  if (!isSupported()) return { supported: false, permission: 'denied', subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = Boolean(sub);
    }
  } catch {}
  return { supported: true, permission, subscribed };
}

async function registerServiceWorker() {
  // Register at root scope so push events fire regardless of which
  // app route the user lands on.
  return navigator.serviceWorker.register(SW_URL, { scope: '/' });
}

async function fetchVapidPublicKey() {
  const data = await request('/notifications/vapid-public-key');
  const key = (data?.publicKey || '').trim();
  if (!key) throw new Error('Push is not enabled for this server (no VAPID key configured).');
  return key;
}

// The Push API needs the VAPID public key as a Uint8Array. The
// server hands it back URL-safe base64 — convert to the raw bytes
// the spec expects.
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

// Convert a PushSubscription into the shape our API expects.
function serializeSubscription(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
  };
}

export async function enable() {
  if (!isSupported()) throw new Error('Push notifications are not supported in this browser.');

  // Permission needs to be requested in response to a user gesture
  // (Chrome/Safari block prompts in background timers). The caller
  // is responsible for triggering enable() from a click handler.
  const permission = Notification.permission === 'default'
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Permission denied. Enable notifications in your browser settings to opt back in.'
      : 'Permission not granted.');
  }

  const reg = await registerServiceWorker();
  // Wait until the registration's active worker is ready — calling
  // pushManager.subscribe() before activation throws in Firefox.
  await navigator.serviceWorker.ready;

  // Reuse the existing subscription if we already have one — push
  // services treat re-subscribing as a no-op but it's cleaner to
  // skip the round trip.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const vapid = await fetchVapidPublicKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid)
    });
  }

  const payload = serializeSubscription(sub);
  await request('/notifications/device-tokens', jsonOptions('POST', {
    subscription: payload,
    platform: 'web'
  }));

  return { subscribed: true };
}

export async function disable() {
  if (!isSupported()) return { subscribed: false };
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return { subscribed: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { subscribed: false };

  const endpoint = sub.endpoint;
  // Local unsubscribe + server-side disable in parallel — if one
  // fails the other still helps reduce phantom rows.
  await Promise.allSettled([
    sub.unsubscribe(),
    request('/notifications/device-tokens', { method: 'DELETE', body: JSON.stringify({ token: endpoint }) })
  ]);
  return { subscribed: false };
}
